import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Advance the slider, from whatever is actually there.
 *
 * The header arrow used to forward its click to the [data-cps-next] nav
 * button. That button is commented out in wf-recently-viewed-carousel.liquid,
 * so the lookup came back null, the wiring bailed before binding, and the arrow
 * did nothing at all — no error, just a dead control.
 *
 * Swiper keeps its instance on the element as _collectionProductSwiper (see
 * section-collection-product-slider.js), so drive it directly and treat the nav
 * button as the optional thing it is. Resolved on click rather than at wiring
 * time, because Swiper often initialises after this runs.
 *
 * @param {HTMLElement} root
 */
function advanceRecentlyViewed(root) {
  const slideNext = root.querySelector('[data-cps-next]');
  if (slideNext instanceof HTMLElement) {
    slideNext.click();
    return;
  }

  const swiperEl = root.querySelector('.collection-product-slider__swiper');
  const swiper = swiperEl && swiperEl._collectionProductSwiper;
  if (swiper && typeof swiper.slideNext === 'function') swiper.slideNext();
}

/**
 * @param {HTMLElement} root
 */
function wireRecentlyViewedHeaderNext(root) {
  const headerNext = root.querySelector('[data-recently-viewed-header-next]');
  if (!(headerNext instanceof HTMLElement)) return;
  if (headerNext.dataset.wfRvNextBound === '1') return;
  headerNext.dataset.wfRvNextBound = '1';
  headerNext.addEventListener('click', () => advanceRecentlyViewed(root));
}

/**
 * @param {ParentNode} [scope]
 */
function wireAllRecentlyViewedHeaderNext(scope = document) {
  scope.querySelectorAll('[data-recently-viewed-slider]').forEach(wireRecentlyViewedHeaderNext);
}

/**
 * @param {string[]} ids
 */
function buildRecentlyViewedSearchUrl(ids) {
  const searchPath = window.Theme?.routes?.search_url ?? '/search';
  const url = new URL(searchPath, window.location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${String(id).trim()}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');
  return url;
}

/**
 * Frames to keep retrying before giving up. This section hydrates behind a
 * fetch, and swiper.min.js is a deferred script, so either can win the race;
 * three seconds covers a slow library load without spinning forever.
 */
const RECENTLY_VIEWED_SLIDE_WAIT_FRAMES = 180;

/**
 * Initialize Swiper once the slides, the library and the init helper all exist.
 *
 * Every one of those three is a moving part that used to be handled
 * differently: slides were retried, a missing `window.Swiper` was deferred once
 * to window load, and a missing `wfInitCollectionProductSwiper` just returned —
 * ending the attempt for good. In practice the carousel often never
 * initialised at all: the section renders, the slides are there, and the
 * arrow moves nothing because there is no Swiper behind it.
 *
 * So retry on any of them, and keep going until the instance actually exists
 * rather than assuming the call worked.
 *
 * @param {HTMLElement | null} sliderRoot
 * @param {number} [attempt]
 */
function initRecentlyViewedSwiper(sliderRoot, attempt = 0) {
  if (!sliderRoot || attempt > RECENTLY_VIEWED_SLIDE_WAIT_FRAMES) return;

  const retry = () => requestAnimationFrame(() => initRecentlyViewedSwiper(sliderRoot, attempt + 1));

  const swiperEl = sliderRoot.querySelector('.collection-product-slider__swiper');
  const slideCount = swiperEl ? swiperEl.querySelectorAll('.swiper-slide').length : 0;
  if (!swiperEl || slideCount === 0) return retry();

  // Already running: leave it alone rather than tearing it down mid-scroll.
  if (swiperEl._collectionProductSwiper) return;

  if (typeof window.wfInitCollectionProductSwiper !== 'function' || typeof window.Swiper !== 'function') {
    return retry();
  }

  window.wfInitCollectionProductSwiper(sliderRoot);

  // The helper returns nothing and bails quietly on its own preconditions, so
  // the instance is the only honest signal that it worked.
  if (!swiperEl._collectionProductSwiper) return retry();
}

/**
 * @param {HTMLElement} pendingEl
 */
async function hydrateRecentlyViewedFromPending(pendingEl) {
  const sectionId = pendingEl.dataset.sectionId;
  if (!sectionId) return;

  const sectionWrapper = document.getElementById(`shopify-section-${sectionId}`);
  if (!sectionWrapper) return;

  const ids = RecentlyViewed.getProducts().filter(Boolean);
  if (ids.length === 0) {
    sectionWrapper.hidden = true;
    return;
  }

  const url = buildRecentlyViewedSearchUrl(ids);

  try {
    await sectionRenderer.renderSection(sectionId, { cache: false, url });
  } catch {
    sectionWrapper.hidden = true;
    return;
  }

  sectionWrapper.hidden = false;

  const sliderRoot = sectionWrapper.querySelector('[data-recently-viewed-slider]');
  if (sliderRoot instanceof HTMLElement) {
    initRecentlyViewedSwiper(sliderRoot);
    wireRecentlyViewedHeaderNext(sliderRoot);
  }
}

function initRecentlyViewedSections() {
  wireAllRecentlyViewedHeaderNext();

  document.querySelectorAll('[data-recently-viewed-root]').forEach((root) => {
    if (root instanceof HTMLElement) hydrateRecentlyViewedFromPending(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecentlyViewedSections);
} else {
  initRecentlyViewedSections();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  wireAllRecentlyViewedHeaderNext(event.target);
  event.target.querySelectorAll('[data-recently-viewed-root]').forEach((root) => {
    if (root instanceof HTMLElement) hydrateRecentlyViewedFromPending(root);
  });
});
